import React, { useContext, useEffect, useMemo, useState } from "react";
import QuestContext from "../../../../components/QuestWrapper";
import "react-toastify/dist/ReactToastify.css";
import EmailLogin from "./EmailLogin";
import GoogleLogin from "./GoogleLogin";
import LoginApi from "./loginApi";
import { QuestLoginProps } from "./logintypes";
import "./Login.css";
import QuestLabs from "../../../../components/QuestLabs";
import LoginThemeManager from "./LoginThemeManager";
import { useGeneral } from "../../../../general";

const QuestLogin: React.FC<QuestLoginProps> = ({
  googleClientId,
  redirectUri,
  redirectURL,
  email = false,
  google = false,
  onSubmit,
  styleConfig,
  IconColor,
  headingText,
  descriptionText,
  googleButtonText,
  showFooter = true,
  campaignId,
  questId,
  campaignToken,
  variations,
  onError,
  BrandTheme,
  accent,
  QuestThemeData,
  redirectURLonGoogle,
  redirectURLonOTP,
  isOffline,
}) => {
  const [isEmail, setIsEmail] = useState<boolean>(false);
  const [isGoogle, setIsGoogle] = useState<boolean>(false);
  const [otpScreen, setOtpScreen] = useState<boolean>(false);
  const [brandTheme, setBrandTheme] = useState(
    BrandTheme
  );
  const [loading, setLoading] = useState(true)
  const [questThemeData, setQuestThemeData] = useState(
    {
      ...QuestThemeData,
      accentColor: QuestThemeData?.accentColor
      ? QuestThemeData?.accentColor
      : accent,
    }
  );
  const [showFooterApi, setShowFooterApi] = useState(isOffline ? showFooter : true);
  const [headingTextApi,setHeadingTextApi] = useState(headingText);
  const [descriptionTextApi,setDescriptionTextApi] = useState(descriptionText);
  const [googleButtonTextApi,setGoogleButtonTextApi] = useState(googleButtonText);
  const [googleClientIdApi, setGoogleClientIdApi] = useState(googleClientId);
  const [redirectionURLonGoogleApi, setRedirectionURLonGoogleApi] = useState(redirectUri || redirectURLonGoogle);
  const [redirectionURLonOTPApi, setRedirectionURLonOTPApi] = useState(redirectURL || redirectURLonOTP);

  const [selectedCampaignVariationId, setSelectedCampaignVariationId] = useState(variations || "");

  useEffect(() => {
    if (isOffline) {
      setShowFooterApi(showFooter)
      setHeadingTextApi(headingText)
      setDescriptionTextApi(descriptionText)
      setGoogleButtonTextApi(googleButtonText)
      setGoogleClientIdApi(googleClientId)
      setRedirectionURLonGoogleApi(redirectUri || redirectURLonGoogle)
      setRedirectionURLonOTPApi(redirectURL || redirectURLonOTP)
      setIsGoogle(google)
      setIsEmail(email)
    }
  }, [isOffline, showFooter, headingText, descriptionText, googleButtonText, googleClientId, google, email])

  const { apiKey, apiSecret, entityId, apiType, themeConfig } = useContext(
    QuestContext.Context
  );
  const loginApi = new LoginApi(apiType);

  const themeManager = useMemo(() => {
    return new LoginThemeManager(
      themeConfig,
      styleConfig,
      questThemeData,
      brandTheme
    );
  }, [themeConfig, styleConfig, questThemeData, brandTheme]);

  useEffect(() => {
    setBrandTheme(BrandTheme || {});
    setQuestThemeData({
      ...QuestThemeData,
      accentColor: QuestThemeData?.accentColor ? QuestThemeData.accentColor : accent,
    });
  }, [BrandTheme, QuestThemeData]);

  useEffect(() => {
    loginApi.trackEvent("quest_Login_loaded", "quest_login");
    setIsEmail(email);
    setIsGoogle(google);

    if (!google && !email) {
      setIsGoogle(true);
      setIsEmail(true);
    }
  }, [entityId, email, google]);

  useEffect(() => {
    const getLoginCampaign = async () => {
      if (isOffline) {
        if (accent) {
          setQuestThemeData({
            ...questThemeData,
            accentColor: questThemeData.accentColor || accent,
          });
          themeManager.updateQuestTheme({
            ...questThemeData,
            accentColor: questThemeData.accentColor || accent,
          });
        }
        setLoading(false)
        return
      };
      if (!entityId || !apiKey) return;

      const ApiHeaders = {
        apiKey,
        apiSecret,
        userId: 'u-0000000000'
      };

      const response = await loginApi.getLoginCampaignResults(
        entityId,
        questId || campaignId || "",
        ApiHeaders,
        variations
      );

      if (response?.campaignVariationId) {
        setSelectedCampaignVariationId(response.campaignVariationId);
      }
      if (response?.brandTheme?.properties) {
        setBrandTheme(response.brandTheme.properties);
        themeManager.updateBrandTheme(response.brandTheme.properties);
      }

      if (response?.questThemeData) {
        setQuestThemeData({
          ...response.questThemeData,
          accentColor: response.questThemeData.accentColor
            ? response.questThemeData.accentColor
            : accent,
        });
        themeManager.updateQuestTheme({
          ...response.questThemeData,
          accentColor: response.questThemeData.accentColor
            ? response.questThemeData.accentColor
            : accent,
        });
      }
      setShowFooterApi(response?.showFooter !== undefined ? response.showFooter : true);
      setHeadingTextApi(response?.uiProps?.headingText || headingText)
      setDescriptionTextApi(response?.uiProps?.descriptionText || descriptionText)
      setGoogleButtonTextApi(response?.uiProps?.googleButtonText || googleButtonText)
      setGoogleClientIdApi(response?.uiProps?.googleClientId || googleClientId)
      setIsGoogle(response?.uiProps?.google || google)
      setRedirectionURLonGoogleApi(response?.uiProps?.redirectURLonGoogle || response?.uiProps?.redirectUri || redirectURLonGoogle || redirectUri)
      setRedirectionURLonOTPApi(response?.uiProps?.redirectURLonOTP || response?.uiProps?.redirectURL || redirectURL || redirectURLonOTP)
      setLoading(false)
    };

    getLoginCampaign();
  }, [campaignId, campaignToken, isOffline, accent]);

  const handleOtp = (val: boolean) => {
    setOtpScreen(val);
  };

  if (loading) {
    return null
  }

  return (
    <div
      className="q-login-container"
      style={themeManager.getContainerStyles()}
    >
      <div className="q-login-body">
        {!otpScreen && (
          <div className="q-login-header">
            <div className="q-login-h1" style={themeManager.getHeadingStyles()}>
              {headingTextApi || "Log in or sign up"}
            </div>
            <div
              className="q-login-h4"
              style={themeManager.getDescriptionStyles()}
            >
              {descriptionTextApi || "Welcome Back, Please enter your details"}
            </div>
          </div>
        )}

        {isEmail && (
          <div className="q-login-mid-cont">
            <EmailLogin
              apiKey={apiKey}
              apiSecret={apiSecret || ""}
              redirectURL={redirectionURLonOTPApi || ""}
              entityId={entityId}
              handleOtp={handleOtp}
              redirectUri={redirectionURLonGoogleApi}
              onSubmit={onSubmit}
              otpScreen={otpScreen}
              setOtpScreen={setOtpScreen}
              styleConfig={styleConfig}
              IconColor={IconColor}
              onError={onError}
              themeManager={themeManager}
              campaignId={campaignId || questId || ""}
              campaignVariationId={selectedCampaignVariationId}
            />

            {!otpScreen && isGoogle && (
              <>
                <div
                  className="q-login-or-container"
                  style={themeManager.getOrDividerStyles()}
                >
                  <div
                    className="login-or-line"
                    style={themeManager.getOrDividerStyles().line}
                  />
                  <div
                    className="q-or-continue"
                    style={themeManager.getORTextStyles()}
                  >
                    OR
                  </div>
                  <div
                    className="login-or-line"
                    style={themeManager.getOrDividerStyles().line}
                  />
                </div>

                <GoogleLogin
                  entityId={entityId}
                  redirectUri={redirectionURLonGoogleApi || ""}
                  redirectURL={redirectionURLonOTPApi || ""}
                  googleClientId={googleClientIdApi || ""}
                  apiSecret={apiSecret || ""}
                  apiKey={apiKey}
                  onSubmit={onSubmit}
                  googleButtonText={googleButtonTextApi}
                  onError={onError}
                  themeManager={themeManager}
                />
              </>
            )}
          </div>
        )}

        {!isEmail && isGoogle && (
          <GoogleLogin
            entityId={entityId}
            redirectUri={redirectionURLonGoogleApi || ""}
            redirectURL={redirectionURLonOTPApi || ""}
            googleClientId={googleClientIdApi || ""}
            apiSecret={apiSecret || ""}
            apiKey={apiKey}
            onSubmit={onSubmit}
            googleButtonText={googleButtonTextApi}
            onError={onError}
            themeManager={themeManager}
          />
        )}
      </div>

      <div className="quest_footer">
        {showFooterApi && (
          <QuestLabs
            style={themeManager.getFooterStyles().container}
            textStyle={themeManager.getFooterStyles().text}
            iconStyle={themeManager.getFooterStyles().icon}
            text={themeManager.getFooterStyles().content.text}
            link={themeManager.getFooterStyles().content.link}
          />
        )}
      </div>
    </div>
  );
};

export default QuestLogin;
